import type { LeadCaptureData } from '@/types';
import { isValidName, isValidPhone, isValidCity, isValidEmail } from '@/utils/validators';
import { APP_CONFIG } from '@/config/constants';

export interface LeadCaptureScreenOptions {
  onSubmit: (data: LeadCaptureData) => void;
}

export class LeadCaptureScreen {
  private container: HTMLElement;
  private options: LeadCaptureScreenOptions;
  private form: HTMLFormElement | null = null;

  constructor(parentElement: HTMLElement, options: LeadCaptureScreenOptions) {
    this.options = options;
    this.container = document.createElement('div');
    this.container.className = 'lead-capture-screen';

    this.render();
    parentElement.appendChild(this.container);
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="lead-form-container">
        <div class="form-logo">
          <img src="${APP_CONFIG.LOGO_PATH}" alt="${APP_CONFIG.COMPANY_NAME}">
        </div>
        <h2>Vamos começar!</h2>
        <p class="subtitle">Preencha seus dados para iniciar o diagnóstico</p>

        <form id="lead-form">
          <div class="form-group">
            <label for="nome">Nome completo</label>
            <input type="text" id="nome" name="nome" placeholder="Seu nome" required>
            <span class="error-message" id="nome-error"></span>
          </div>

          <div class="form-group">
            <label for="email">E-mail</label>
            <input type="email" id="email" name="email" placeholder="seu@email.com" required>
            <span class="error-message" id="email-error"></span>
          </div>

          <div class="form-group">
            <label for="telefone">Telefone (WhatsApp)</label>
            <input type="tel" id="telefone" name="telefone" placeholder="(11) 99999-9999" required>
            <span class="error-message" id="telefone-error"></span>
          </div>

          <div class="form-group">
            <label for="cidade">Cidade</label>
            <input type="text" id="cidade" name="cidade" placeholder="Sua cidade" required>
            <span class="error-message" id="cidade-error"></span>
          </div>

          <div style="position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;">
            <label for="company_website">Company website</label>
            <input type="text" id="company_website" name="company_website" autocomplete="off" tabindex="-1">
          </div>

          <button type="submit" class="btn-submit">
            <span>Iniciar Diagnóstico</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </button>
        </form>
      </div>
    `;

    this.form = this.container.querySelector('#lead-form') as HTMLFormElement;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.form) return;

    const phoneInput = this.form.querySelector('#telefone') as HTMLInputElement;
    phoneInput.addEventListener('input', () => this.formatPhone(phoneInput));

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });
  }

  private formatPhone(input: HTMLInputElement): void {
    let value = input.value.replace(/\D/g, '');

    if (value.length > 11) {
      value = value.slice(0, 11);
    }

    if (value.length > 6) {
      value = value.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    } else if (value.length > 2) {
      value = value.replace(/(\d{2})(\d{0,5})/, '($1) $2');
    } else if (value.length > 0) {
      value = value.replace(/(\d{0,2})/, '($1');
    }

    input.value = value;
  }

  private handleSubmit(): void {
    if (!this.form) return;

    const nomeInput = this.form.querySelector('#nome') as HTMLInputElement;
    const emailInput = this.form.querySelector('#email') as HTMLInputElement;
    const telefoneInput = this.form.querySelector('#telefone') as HTMLInputElement;
    const cidadeInput = this.form.querySelector('#cidade') as HTMLInputElement;
    const honeypotInput = this.form.querySelector('#company_website') as HTMLInputElement;

    const nome = nomeInput.value.trim();
    const email = emailInput.value.trim();
    const telefone = telefoneInput.value.trim();
    const cidade = cidadeInput.value.trim();
    const honeypot = honeypotInput?.value ?? '';

    let hasError = false;

    this.clearErrors();

    if (!isValidName(nome)) {
      this.showError('nome', 'Por favor, insira um nome válido');
      hasError = true;
    }

    if (!isValidEmail(email)) {
      this.showError('email', 'Por favor, insira um e-mail válido');
      hasError = true;
    }

    if (!isValidPhone(telefone)) {
      this.showError('telefone', 'Por favor, insira um telefone válido');
      hasError = true;
    }

    if (!isValidCity(cidade)) {
      this.showError('cidade', 'Por favor, insira uma cidade válida');
      hasError = true;
    }

    if (!hasError) {
      this.options.onSubmit({ nome, email, telefone, cidade, honeypot });
    }
  }

  private showError(field: string, message: string): void {
    const input = this.form?.querySelector(`#${field}`) as HTMLInputElement;
    const errorSpan = this.form?.querySelector(`#${field}-error`) as HTMLSpanElement;

    if (input) {
      input.classList.add('error');
    }
    if (errorSpan) {
      errorSpan.textContent = message;
    }
  }

  private clearErrors(): void {
    const inputs = this.form?.querySelectorAll('input');
    const errors = this.form?.querySelectorAll('.error-message');

    inputs?.forEach(input => input.classList.remove('error'));
    errors?.forEach(error => (error.textContent = ''));
  }

  setLoading(isLoading: boolean): void {
    const submitBtn = this.form?.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = isLoading;
      const span = submitBtn.querySelector('span');
      if (span) {
        span.textContent = isLoading ? 'Carregando...' : 'Iniciar Diagnóstico';
      }
    }
  }

  remove(): void {
    this.container.remove();
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
